import React, { useState } from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Container,
  Paper,
  Link,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import UserDetailsForm from './UserDetailsForm';
import { isValidPhoneNumber } from 'react-phone-number-input';
import CompanyDetailsForm from './CompanyDetailsForm';
import LoadingRedirect from './LoadingRedirect'; // Import our new component
import { checkEmailExists, submitUserProfile, submitCompanyDetails } from '../../api';
import { VALIDATE_PROMO_CODE_ENDPOINT } from '../../config/urls';
import Logo from '../Logo.js';
import Footer from '../Footer.js';
import BackButton from '../../common/back-button.jsx';

// Updated steps to include the loading/redirect page
const steps = ['Profile Setup', 'Company Details', 'Finalizing'];

const TenantOnboardingForm = ({promoCode = null}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [leadId, setLeadId] = useState(null);
  const [userData, setUserData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: 'Executive',
    waPlan: '',
    channel: '',
    countryCode: "IN",
    promoCode: promoCode || '',
  });
  const [companyData, setCompanyData] = useState({
    companyName: '',
    industry: 'Technology',
    size: '',
    website: '',
    country: '',
    state: '',
    gst: '',
    primaryFocus: 'B2B (Business To Business)',
    hearAboutUs: 'Search Engine'
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleUserDataChange = (field, value) => {
    const nextUserData = {
      ...userData,
      [field]: value
    };

    // Keep channel valid when plan switches to ones
    // where "Both" is not an allowed option.
    if (
      field === 'waPlan' &&
      ['engage', 'automate'].includes(String(value || '').toLowerCase()) &&
      String(nextUserData.channel || '').toLowerCase() === 'both'
    ) {
      nextUserData.channel = '';
    }

    setUserData(nextUserData);
    
    // Clear error for this field when user types
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: null
      });
    }
  };

  const handlePromoCodeBlur = async () => {
    const code = (userData.promoCode || '').trim();
    if (!code) return;

    try {
      const response = await fetch(
        `${VALIDATE_PROMO_CODE_ENDPOINT}?promo_code=${encodeURIComponent(code)}`
      );
      const data = await response.json();
      if (!data.valid) {
        setErrors((prev) => ({ ...prev, promoCode: data.message }));
      } else {
        setErrors((prev) => ({ ...prev, promoCode: null }));
      }
    } catch (error) {
      console.error('Error validating promo code:', error);
    }
  };

  const handleCompanyDataChange = (field, value) => {
    // If changing country to something other than India, clear GST
    if (field === 'country' && value !== 'India') {
      setCompanyData({
        ...companyData,
        [field]: value,
        gst: '' // Clear GST when country is not India
      });
    } else {
      setCompanyData({
        ...companyData,
        [field]: value
      });
    }
    
    // Clear error for this field when user types
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: null
      });
    }
  };

  const validateUserDetails = async () => {
    const newErrors = {};
    
    // Basic validation
    if (!userData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!userData.lastName.trim()) newErrors.lastName = 'Last name is required';
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!userData.email.trim()) {
      newErrors.email = 'Work email is required';
    } else if (!emailRegex.test(userData.email)) {
      newErrors.email = 'Please enter a valid email';
    } else {
      // Check if email already exists in the system
      try {
        setLoading(true);
        const exists = await checkEmailExists(userData.email);
        
        if (exists) {
          newErrors.email = 'Looks like you are already a BotBridge user. Please login';
          newErrors.emailAction = 'login';
        }
      } catch (error) {
        console.error('Error checking email:', error);
      } finally {
        // setLoading(false);
      }
    }
    
    if (!userData.countryCode) newErrors.countryCode = 'Country code is required';
    if (!userData.phone.trim()) {
      newErrors.phone = 'Contact phone number is required';
    } else {
      // The library expects the number to be evaluated against the selected country code
      const isValid = isValidPhoneNumber(userData.phone, userData.countryCode);
      if (!isValid) {
        newErrors.phone = 'Please enter a valid phone number for the selected country';
      }
    }
    if (!userData.role) newErrors.role = 'Your role is required';
    if (!userData.waPlan) newErrors.waPlan = 'WA Plan is required';
    if (!userData.channel) newErrors.channel = 'Channel is required';

    if (userData.promoCode && userData.promoCode.trim()) {
      try {
        const response = await fetch(
          `${VALIDATE_PROMO_CODE_ENDPOINT}?promo_code=${encodeURIComponent(userData.promoCode.trim())}` 
        );
        const data = await response.json();
        if (!data.valid) {
          newErrors.promoCode = data.message || 'Invalid promo code. Please check the code and try again.';
        }
      } catch (error) {
        console.error('Error validating promo code:', error);
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateCompanyDetails = () => {
    const newErrors = {};
    
    if (!companyData.companyName.trim()) newErrors.companyName = 'Company name is required';
    if (!companyData.industry) newErrors.industry = 'Industry is required';
    if (!companyData.size) newErrors.size = 'Company size is required';
    
    // Updated URL validation logic - REQUIRES proper domain with TLD
    if (!companyData.website.trim()) {
      newErrors.website = 'Company website is required';
    } else {
      // Clean and normalize the URL
      let websiteUrl = companyData.website.trim();
      
      // If no protocol is provided, assume https://
      if (!websiteUrl.match(/^https?:\/\//i)) {
        websiteUrl = 'https://' + websiteUrl;
      }
      
      // More comprehensive URL validation
      try {
        const url = new URL(websiteUrl);
        
        // STRICTER domain validation - requires at least one dot (TLD)
        const strictDomainRegex = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
        
        if (!strictDomainRegex.test(url.hostname)) {
          // newErrors.country = 'Country location is required';
          newErrors.website = 'Please enter a valid website URL (e.g., company.com)';
        }
      } catch (error) {
        newErrors.website = 'Please enter a valid website URL';
      }
    }
    
    if (!companyData.country) newErrors.country = 'Country location is required';
    
    // GST validation only for India
    if (companyData.country === 'India') {
      const gstRegex = /\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}[Z]{1}[A-Z\d]{1}/;
      // Check for all zeros
      const allZerosRegex = /^0+$/;
      
      if (!companyData.gst.trim()) {
        // newErrors.gst = 'GST is required for Indian companies';
        console.log("GST is not required for Indian companies");
      } else if (!gstRegex.test(companyData.gst)) {
        newErrors.gst = 'Please enter a valid Indian GST number';
      } else if (allZerosRegex.test(companyData.gst.replace(/[^0-9]/g, ''))) {
        newErrors.gst = 'GST number cannot contain all zeros in numeric portions';
      }
    } 
    
    if (!companyData.primaryFocus) newErrors.primaryFocus = 'Primary focus is required';
    if (!companyData.hearAboutUs) newErrors.hearAboutUs = 'This field is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (activeStep === 0) {
      // setLoading(true);
      const isValid = await validateUserDetails();
      
      if (isValid) {
        // First submit the user details to the backend
        try {
          const response = await submitUserProfile(userData);
          
          if (response.success) {
            // Store the lead ID for the next step
            setLeadId(response.lead_id);
            setActiveStep((prevStep) => prevStep + 1);
          } else {
            setSnackbar({
              open: true,
              message: response.error || 'An error occurred while saving your profile',
              severity: 'error'
            });
          }
        } catch (error) {
          console.error('Error in profile submission:', error);
          setSnackbar({
            open: true,
            message: 'An unexpected error occurred',
            severity: 'error'
          });
        }
      }
      setLoading(false);
    } else if (activeStep === 1) {
      const isValid = validateCompanyDetails();
      if (isValid) {
        handleSubmit();
      }
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  // Backend validation errors are keyed by API field name; the form uses its own names
  const COMPANY_FIELD_NAMES = {
    company_name: 'companyName',
    company_size: 'size',
    primary_focus: 'primaryFocus',
    hear_about_us: 'hearAboutUs',
  };

  // Starts the tenant build. It takes longer than the gateway timeout, so the request isn't
  // awaited: the setup screen polls the onboarding status instead. Only a quick validation
  // error (400) sends the user back to the company step. Safe to call again for a retry,
  // since the backend never builds a second tenant for the same lead.
  const startProvisioning = () => {
    submitCompanyDetails(companyData, leadId).then((response) => {
      if (response.status !== 400) return;

      const fieldErrors = {};
      Object.entries(response.data || {}).forEach(([field, messages]) => {
        fieldErrors[COMPANY_FIELD_NAMES[field] || field] = Array.isArray(messages) ? messages[0] : messages;
      });
      setErrors(fieldErrors);
      setActiveStep(1);
      setSnackbar({
        open: true,
        message: fieldErrors.gst || fieldErrors.error || 'Please check your company details',
        severity: 'error'
      });
    });
  };

  const handleSubmit = () => {
    startProvisioning();
    setActiveStep(2);
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <UserDetailsForm
            userData={userData}
            errors={errors}
            onChange={handleUserDataChange}
            promoCode={promoCode}
            onPromoBlur={handlePromoCodeBlur}
          />
        );
      case 1:
        return (
          <CompanyDetailsForm 
            companyData={companyData} 
            errors={errors}
            onChange={handleCompanyDataChange} 
          />
        );
      case 2:
        // This is our new loading/redirect step
        return <LoadingRedirect leadId={leadId} onRetry={startProvisioning} />;
      default:
        return 'Unknown step';
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: {xs: 2, md: 4} }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Logo />
        </Box>
        <Typography variant="h5" align="center" gutterBottom>
          Create Account
        </Typography>
        <hr />
        <Stepper activeStep={activeStep} sx={{ pt: 3, pb: {xs: 3, md: 5} }}>
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel
                StepIconProps={{
                  style: {
                    color: activeStep >= index ? '#1976d2' : '#bdbdbd',
                  }
                }}
                style={{
                  color: activeStep >= index ? '#1976d2' : '#bdbdbd',
                }}
                sx={{
                  flexDirection: {xs: "column", md: "row"},
                  alignItems: {xs: "center", md: "flex-start"},
                  textAlign: {xs: "center", md: "left"},
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {getStepContent(activeStep)}
        
        {/* Only show navigation buttons for steps 0 and 1 */}
        {activeStep < 2 && (
          <Box sx={{ 
              display: 'flex', 
              flexDirection: {xs:'column', md: 'row'}, 
              justifyContent: 'space-between', 
              alignItems: {xs: 'flex-start', md:'center'}, 
              mt: 3 
            }}
          >
            {/* Left side with login link */}
            <Button 
              variant="text" 
              color="primary" 
              component={Link} 
              href="/login" 
              sx={{ textTransform: 'none' }}
              disabled={loading}
            >
              Already have an account? Login
            </Button>
            
            {/* Right side with existing navigation buttons */}
            <Box sx={{ display: 'flex', justifyContent: {xs: "space-between", md: "flex-end"}, width:{xs: "100%", md: "auto"} }}>
              {activeStep !== 0 && (
                <BackButton 
                  onClick={handleBack} 
                 />
              )}
              
              <Button
                variant="contained"
                color="primary"
                onClick={handleNext}
                disabled={
                  loading ||
                  (activeStep === 0 && (
                    !userData.firstName || 
                    !userData.lastName || 
                    !userData.email || 
                    !userData.phone || 
                    !userData.role || 
                    !userData.waPlan ||
                    !userData.channel
                  )) || 
                  (activeStep === 1 && (
                    !companyData.companyName || 
                    !companyData.website ||
                    !companyData.industry ||
                    !companyData.size ||
                    !companyData.country ||
                    // GST is only required for India
                    // (companyData.country === 'India' && !companyData.gst) ||
                    !companyData.primaryFocus ||
                    !companyData.hearAboutUs
                  ))
                }
                sx={{
                  bgcolor: (activeStep === 0 && (
                    !userData.firstName || 
                    !userData.lastName || 
                    !userData.email || 
                    !userData.phone || 
                    !userData.role || 
                    !userData.waPlan ||
                    !userData.channel
                  )) || 
                  (activeStep === 1 && (
                    !companyData.companyName || 
                    !companyData.website ||
                    !companyData.industry ||
                    !companyData.size ||
                    !companyData.country ||
                    // GST is only required for India
                    // (companyData.country === 'India' && !companyData.gst) ||
                    !companyData.primaryFocus ||
                    !companyData.hearAboutUs
                  )) ? '#bdbdbd' : '#1976d2',
                  ml: {xs: "auto", md: 0},
                }}
                startIcon={loading && <CircularProgress size={20} color="inherit" />}
              >
                {activeStep === steps.length - 2 ? 'Create Account' : 'Continue'}
              </Button>
            </Box>
          </Box>
        )}
        <Footer />
      </Paper>
      
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar({...snackbar, open: false})}
      >
        <Alert onClose={() => setSnackbar({...snackbar, open: false})} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default TenantOnboardingForm;